import moment from 'moment';

import { UserRole } from '../../../../../shared/enums';
import sortByFullName from '../../../helpers/reactTable/sortMethods';
import { getDaysLeft, getCurrentMilestoneName } from '../../../helpers/timeline';

const convertTimeStr = (a) => {
  if (a) {
    const tempArr = a.split(' ');
    let formattedDigit = 0;
    if (tempArr[1].includes('day')) {
      formattedDigit = Number(tempArr[0]) * 24;
    } else if (tempArr[1].includes('hour')) {
      formattedDigit = Number(tempArr[0]);
    } else if (tempArr[1].includes('minute')) {
      formattedDigit = Number(tempArr[0]) / 60;
    } else {
      formattedDigit = Number(tempArr[0]) / 60 / 60;
    }
    return formattedDigit;
  }
  return 0;
};

const sortDate = (rowA, rowB, id) => (convertTimeStr(rowA.original[id]) > convertTimeStr(rowB.original[id]) ? 1 : -1);

// const sortFee = (rowA, rowB, id) => {
//   let valueA = rowA.original[id] || 0;
//   let valueB = rowB.original[id] || 0;
//   if (typeof valueA === 'string') {
//     [, valueA] = rowA.original[id].split(' ');
//     valueA = Number.parseInt(valueA, 10);
//   }
//   if (typeof valueB === 'string') {
//     [, valueB] = rowB.original[id].split(' ');
//     valueB = Number.parseInt(valueB, 10);
//   }
//   return valueA > valueB ? 1 : -1;
// };

const columnsFixtures = {
  project: {
    Header: 'Project',
    accessor: 'customer',
    sortType: sortByFullName,
    customFieldMapping: {
      tableAccessor: 'customer',
      documentKey: 'customer.fullName',
    },
  },
  editor: {
    Header: 'Editor',
    accessor: 'editor',
    sortType: sortByFullName,
    customFieldMapping: {
      tableAccessor: 'editor',
      documentKey: 'editor.fullName',
    },
  },
  writer: {
    Header: 'Writer',
    accessor: 'writer',
    sortType: sortByFullName,
    customFieldMapping: {
      tableAccessor: 'writer',
      documentKey: 'writer.fullName',
    },
  },
  market: {
    Header: 'Market',
    accessor: 'market',
    disableSortBy: true,
  },
  package: {
    Header: 'Package',
    accessor: 'packageType',
    disableSortBy: true,
  },
  // fee: { // NOTE: ST-3608
  //   Header: 'Fee',
  //   accessor: 'writerFeeDescription',
  //   sortType: sortFee,
  //   disableSortBy: true,
  // },
  currentStage: {
    Header: 'Current stage',
    accessor: 'currentStage',
    customFieldMapping: {
      tableAccessor: 'currentStage',
      documentKey: 'currentStage',
    },
  },
  customerMilestone: {
    Header: 'Customer milestone',
    accessor: 'customerMilestone',
    disableSortBy: true,
  },
  lastUpdate: {
    Header: 'Last update',
    accessor: 'lastUpdate',
    sortType: sortDate,
    customFieldMapping: {
      tableAccessor: 'lastUpdate',
      documentKey: 'currentTimeline',
    },
  },
};

const columnsSchemes = {
  [UserRole.ADMIN]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.writer,
    columnsFixtures.market,
    columnsFixtures.customerMilestone,
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
  [UserRole.EDITOR]: [
    columnsFixtures.project,
    columnsFixtures.writer,
    columnsFixtures.customerMilestone,
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
  [UserRole.WRITER]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.package,
    // columnsFixtures.fee, // NOTE: ST-3608
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
  [UserRole.DEVELOPMENTAL_EDITOR]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.package,
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
  [UserRole.CONTENT_TEAM]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.package,
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
  [UserRole.PRO_PROOFREADER]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.package,
    columnsFixtures.currentStage,
    columnsFixtures.lastUpdate,
  ],
};

const columnsSchemesForMobile = {
  [UserRole.WRITER]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.currentStage,
  ],
  [UserRole.EDITOR]: [
    columnsFixtures.project,
    columnsFixtures.writer,
    columnsFixtures.currentStage,
  ],
  [UserRole.ADMIN]: [
    columnsFixtures.project,
    columnsFixtures.writer,
    columnsFixtures.currentStage,
  ],
  [UserRole.DEVELOPMENTAL_EDITOR]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.currentStage,
  ],
  [UserRole.CONTENT_TEAM]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.currentStage,
  ],
  [UserRole.PRO_PROOFREADER]: [
    columnsFixtures.project,
    columnsFixtures.editor,
    columnsFixtures.currentStage,
  ],
};

const getTableColumns = (userRole, isMobile) => (
  isMobile ? columnsSchemesForMobile[userRole] : columnsSchemes[userRole]
);

const getExcelData = (projects) => {
  if (!projects?.length) return [];
  return projects.map((project) => {
    const { customer, writer } = project;
    const dateOfThelastEvent = project.currentTimelineData[0]?.date || '';
    let customerMilestoneName;
    let daysLeftTillDeadline = '';

    if (project.isProjectTimelineShown) {
      const currentMilestoneName = getCurrentMilestoneName(project.allTimelineData);
      const currentMilestone = project.timelineSchedule.find(({ name }) => name === currentMilestoneName);
      const milestoneDaysLeft = getDaysLeft(currentMilestone.targetDate);

      const isOverdue = milestoneDaysLeft < 0;
      customerMilestoneName = currentMilestoneName;
      daysLeftTillDeadline = `${Math.abs(milestoneDaysLeft)} days ${isOverdue ? 'overdue' : 'left'}`;
    }

    return ({
      customerName: customer.fullName,
      hubspotDealId: project.hubspotDealId,
      createOn: moment(project.createdOn).format('DD MMMM YYYY'),
      extensionFeeDeadline: project.isProjectTimelineShown ? moment(project.extensionFeeDeadline).format('DD MMMM YYYY') : '',
      writerName: writer?.fullName || '',
      customerMilestoneName: customerMilestoneName ? t(`timelineSection.${customerMilestoneName}`, { ns: i18next.Namespace.COMMON }) : '',
      daysLeftTillDeadline,
      currentStage: project.stages[0]?.name,
      updatedOn: moment(dateOfThelastEvent).format('DD MMMM YYYY'),
    });
  });
};

const getExcelColumns = () => ([
  'Customer name',
  'Hubspot Deal ID',
  'Project start date',
  'Extension fee date',
  'Writer name',
  'Customer milestone',
  'Days left till deadline',
  'Current stage',
  'Time since last update',
]);

export default {
  getTableColumns,
  columnsFixtures,
  getExcelData,
  getExcelColumns,
};
